import type { StateCreator } from "zustand";
import type { VendState } from ".";
import {
  addDaysToDateKey,
  addMonthsToDateKey,
  dateKeyBoundsToIso,
  monthEndDateKey,
  nowKeyInTimezone,
  parseDateKeyUTC,
  DATE_RANGE_PRESET_OPTIONS,
  type DateRangePreset,
} from "../timezoneDate";

export type InsightsRange = DateRangePreset;

export const INSIGHTS_RANGE_OPTIONS = DATE_RANGE_PRESET_OPTIONS;

export type SalesAggregate = {
  totalCash: number;
  totalCard: number;
  totalCoin: number;
  intervalStart: string;
  intervalEnd: string;
};

export type InsightsRangeBounds = {
  startIso: string;
  endIso: string;
  startDate: string;
  endDate: string;
};

export function insightsRangeBounds(
  range: InsightsRange,
  timeZone?: string
): InsightsRangeBounds {
  const nowKey = nowKeyInTimezone(timeZone);
  let startDate = nowKey;
  let endDate = nowKey;

  if (range === "yesterday") {
    startDate = addDaysToDateKey(nowKey, -1);
    endDate = startDate;
  } else if (
    range === "this_week" ||
    range === "last_week" ||
    range === "last_2_weeks"
  ) {
    const day = parseDateKeyUTC(nowKey).getUTCDay();
    const offsetToMonday = (day + 6) % 7;
    startDate = addDaysToDateKey(
      nowKey,
      -offsetToMonday -
        (range === "last_week" ? 7 : range === "last_2_weeks" ? 14 : 0)
    );
    endDate = addDaysToDateKey(startDate, range === "last_2_weeks" ? 13 : 6);
  } else if (range === "this_month" || range === "last_month") {
    const baseKey = addMonthsToDateKey(nowKey, range === "last_month" ? -1 : 0, 1);
    startDate = baseKey;
    endDate = monthEndDateKey(baseKey);
  } else if (range === "last_3_months" || range === "last_6_months") {
    endDate = nowKey;
    startDate = addMonthsToDateKey(nowKey, range === "last_3_months" ? -2 : -5, 1);
  } else if (range === "this_year") {
    const year = parseDateKeyUTC(nowKey).getUTCFullYear();
    startDate = `${year}-01-01`;
    endDate = `${year}-12-31`;
  } else if (range === "last_year") {
    const year = parseDateKeyUTC(nowKey).getUTCFullYear() - 1;
    startDate = `${year}-01-01`;
    endDate = `${year}-12-31`;
  } else if (range === "all_time") {
    startDate = "1970-01-01";
    endDate = nowKey;
  }

  const { startIso, endIso } = dateKeyBoundsToIso(startDate, endDate, timeZone);
  return { startIso, endIso, startDate, endDate };
}

export type InsightsSlice = {
  insightsRange: InsightsRange;
  setInsightsRange: (range: InsightsRange) => void;
};

export const createInsightsSlice: StateCreator<
  VendState,
  [],
  [],
  InsightsSlice
> = (set, get) => ({
  insightsRange: "today",
  setInsightsRange: (range) => set({ insightsRange: range } as VendState),
  // insights slice intentionally owns only date-range selection now.
});

// Daily summary dates already belong to the attribution timezone. Do not
// reinterpret them as timestamps or fall back to the API's 1970 lower bound.
export function insightsChartStartDate(
  range: InsightsRange, startDate: string, endDate: string, bucketDates: string[],
): string {
  if (range !== "all_time") return startDate;
  const first = bucketDates.reduce((earliest, date) => date < earliest ? date : earliest, endDate);
  const firstMonth = `${first.slice(0, 7)}-01`;
  const minimum = addMonthsToDateKey(endDate, -5, 1);
  return firstMonth < minimum ? firstMonth : minimum;
}
